import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { uploadAgeCategoryImage, addAgeCategory, editAgeCategory, deleteAgeCategory, getAgeCategoryById } from '../api/tournament/ageCategoryService';
import { DeleteModal } from './DeleteModal';
import ShimmerLoader from './ShimmerLoader';
import './ManageTournamentsFixturesTable.css';
import { getImageUrl } from '../utils/imageUrl';
import { TOURNAMENT_KNOCKOUT_FORMATS } from '../api/tournament/types';
import { normalizeEventCategories } from '../pages/ManageTournamentsFixtures/seasonSetup';
import {
  PICKLEBALL_EVENT_CATEGORIES,
  normalizePickleballEventCategories,
} from '../api/tournament/pickleballFixture';

interface AgeCategory {
  _id: string;
  ageCategory: string;
  standingsType: string;
  format?: string;
  knockoutFormat?: string;
  liveImage?: string;
  highlightsImage?: string;
  tournament?: string;
  qualificationStatus?: string;
  qualifiedTeams?: number;
  duration?: string;
  sports?: string;
  isQualificationApplied?: boolean;
  qualificationTeamCount?: number;
  eventCategories?: string[];
  isKnockoutEnabled?: boolean;
  awardCategory?: string;
  votingInfo?: string;
}

interface AgeCategoryManagementProps {
  ageCategories: AgeCategory[];
  search: string;
  onSearch: (value: string) => void;
  onRefresh: () => void;
  tournamentId: string;
  tournamentName: string;
  tournamentSports: string;
  ageCategoriesLoading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  typeFilter: any;
  setTypeFilter: (option: any) => void;
  formatFilter: any;
  setFormatFilter: (option: any) => void;
  typeOptions: any[];
  formatOptions: any[];
  isStandingsContext?: boolean; // New prop to indicate if user is in Standings tab
  tournamentSeasonId?: string; // Optional: tournamentSeason id for season context
  canConfigureTies?: boolean;
  canConfigureAwards?: boolean;
  canConfigureVoting?: boolean;
}

const AgeCategoryManagement: React.FC<AgeCategoryManagementProps> = ({
  ageCategories,
  search,
  onSearch,
  onRefresh,
  tournamentId,
  tournamentSeasonId,
  tournamentName,
  tournamentSports,
  ageCategoriesLoading,
  page,
  pageSize,
  totalPages,
  totalCount,
  onPageChange,
  onPageSizeChange,
  typeFilter,
  setTypeFilter,
  formatFilter,
  setFormatFilter,
  typeOptions,
  formatOptions,
  isStandingsContext = false,
  canConfigureTies = false,
  canConfigureAwards = false,
  canConfigureVoting = false,
}) => {
  // Helper to get sport - use prop if provided, otherwise throw error
  const getSport = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    throw new Error('Tournament sport is required. Please ensure tournamentSports prop is passed to AgeCategoryManagement.');
  };
  
  // Determine if current tournament sport is Badminton or Table Tennis
  // Used to conditionally hide actions in Standings tab
  const isBadmintonOrTableTennis = (): boolean => {
    try {
      const sport = getSport();
      const normalized = sport.replace(/\s+/g, '').toLowerCase();
      return normalized === 'badminton' || normalized === 'tabletennis' || normalized === 'tt';
    } catch {
      return false;
    }
  };

  const isBadminton = (): boolean => {
    try {
      return getSport().replace(/[\s_-]/g, '').toLowerCase() === 'badminton';
    } catch {
      return false;
    }
  };

  const isPickleball = (): boolean => {
    try {
      return getSport().replace(/[\s_-]/g, '').toLowerCase() === 'pickleball';
    } catch {
      return false;
    }
  };

  const eventCategoriesForForm = (eventCategories: unknown): string[] | string => {
    if (isPickleball()) {
      return normalizePickleballEventCategories(eventCategories);
    }

    return Array.isArray(eventCategories) ? eventCategories.join(', ') : '';
  };
  
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingAgeCategory, setLoadingAgeCategory] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<any>({});

  const [form, setForm] = useState({
    ageCategory: '',
    standingsType: '',
    format: '',
    knockoutFormat: '',
    qualificationStatus: '',
    qualifiedTeams: '',
    duration: '',
    liveImage: '' as string | null,
    highlightsImage: '' as string | null,
    eventCategories: [] as string[] | string,
    isKnockoutEnabled: false,
    awardCategory: '',
    votingInfo: '',
  });

  const [formTouched, setFormTouched] = useState({
    ageCategory: false,
    standingsType: false,
    format: false,
    duration: false,
    qualificationStatus: false,
    eventCategories: false,
  });

  // Options for React-Select dropdowns
  const durationOptions = [
    { value: '30 min', label: '30 min' },
    { value: '45 min', label: '45 min' },
    { value: '1 hr', label: '1 hr' },
    { value: '1.5 hr', label: '1.5 hr' },
    { value: '2 hr', label: '2 hr' }
  ];

  const qualificationStatusOptions = [
    { value: 'Qualified', label: 'Qualified' },
    { value: 'Not Qualified', label: 'Not Qualified' }
  ];

  const getDurationPayloadValue = (duration: unknown): string | undefined => {
    if (duration === null || duration === undefined || duration === '') {
      return undefined;
    }

    const rawValue = typeof duration === 'object' && 'value' in duration
      ? (duration as { value?: unknown }).value
      : duration;

    const durationText = String(rawValue).trim();
    return durationText ? durationText.split(/\s+/)[0] : undefined;
  };

  const knockoutFormatOptions = [
    { value: TOURNAMENT_KNOCKOUT_FORMATS.KNOCKOUT_WITH_BYES, label: 'Knockout with Byes' },
    { value: TOURNAMENT_KNOCKOUT_FORMATS.SINGLE_ELIMINATION, label: 'Single Elimination' }
  ];

  // Use server-side filtered data directly (no client-side filtering needed)
  const filteredAgeCategories = ageCategories;

  // Use server-side pagination data
  const shouldShowPagination = totalPages > 1;

  const handleAdd = () => {
    setShowAddForm(true);
    setEditingId(null);
    setViewMode(false);
    setFormError(null);
    setFormSuccess(null);
    resetForm();
  };

  const handleEdit = async (id: string) => {
    // If in standings context, use in-memory data for navigation (no API call needed)
    if (isStandingsContext) {
      const ageCategory = ageCategories.find(ac => ac._id === id);
      if (ageCategory) {
        const ageCategoryInfo = {
          _id: ageCategory._id,
          ageCategory: ageCategory.ageCategory,
          standingsType: ageCategory.standingsType
        };
        
        if (ageCategory.standingsType === 'single') {
          navigate(`/manage-tournaments-fixtures/standings/single/${tournamentId}/${id}?sport=${encodeURIComponent(getSport())}&seasonId=${encodeURIComponent(tournamentSeasonId || '')}`, {
            state: { 
              ageCategoryInfo,
              tournamentId,
              tournamentSeasonId, // Pass tournamentSeasonId for back navigation
              fromTournamentDetailPage: !tournamentSeasonId || window.location.pathname.includes(`/manage-tournaments-fixtures/${tournamentId}`) && !window.location.pathname.includes('/seasons/') // Check if we're on tournament detail page (not season detail page)
            }
          });
        } else if (ageCategory.standingsType === 'group') {
          navigate(`/manage-tournaments-fixtures/standings/group/${tournamentId}/${id}?sport=${encodeURIComponent(getSport())}&seasonId=${encodeURIComponent(tournamentSeasonId || '')}`, {
            state: { 
              ageCategoryInfo,
              tournamentId,
              tournamentSeasonId, // Pass tournamentSeasonId for back navigation
              fromTournamentDetailPage: !tournamentSeasonId || window.location.pathname.includes(`/manage-tournaments-fixtures/${tournamentId}`) && !window.location.pathname.includes('/seasons/') // Check if we're on tournament detail page (not season detail page)
            }
          });
        } else {
          // Fallback to edit form for other types - fetch from API
          await loadAgeCategoryForEdit(id);
        }
      }
    } else {
      // If in age categories context, fetch from API
      await loadAgeCategoryForEdit(id);
    }
  };

  const loadAgeCategoryForEdit = async (id: string) => {
    setLoadingAgeCategory(true);
    setFormError(null);
    setFormSuccess(null);
    
    try {
      const sport = getSport();
      const response = await getAgeCategoryById(id, sport);
      const ageCategory = response?.data || response;
      
      if (ageCategory) {
        setEditingId(id);
        setViewMode(false);
        setShowAddForm(true);
        setForm({
          ageCategory: ageCategory.ageCategory || '',
          standingsType: ageCategory.standingsType || '',
          format: isPickleball() ? 'player' : ageCategory.format || '',
          knockoutFormat: ageCategory.knockoutFormat || '',
          qualificationStatus: ageCategory.isQualificationApplied === true ? 'Qualified' : ageCategory.isQualificationApplied === false ? 'Not Qualified' : '',
          qualifiedTeams: ageCategory.qualifiedTeams?.toString() || ageCategory.qualificationTeamCount?.toString() || '',
          duration: ageCategory.duration || '',
          liveImage: ageCategory.liveImage || null,
          highlightsImage: ageCategory.highlightsImage || null,
          eventCategories: eventCategoriesForForm(ageCategory.eventCategories),
          isKnockoutEnabled: ageCategory.isKnockoutEnabled || false,
          awardCategory: typeof ageCategory.awardCategory === 'string' ? ageCategory.awardCategory : '',
          votingInfo: typeof ageCategory.votingInfo === 'string' ? ageCategory.votingInfo : '',
        });
        
        // Scroll to top for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setFormError('Age category not found.');
      }
    } catch (err: any) {
      console.error('Error loading age category:', err);
      let errorMessage = 'Failed to load age category. Please try again.';
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setLoadingAgeCategory(false);
    }
  };

  const handleView = (id: string) => {
    const ageCategory = ageCategories.find(ac => ac._id === id);
    if (ageCategory) {
      // If in standings context, navigate to standings detail page
      if (isStandingsContext) {
        const ageCategoryInfo = {
          _id: ageCategory._id,
          ageCategory: ageCategory.ageCategory,
          standingsType: ageCategory.standingsType,
          format: ageCategory.format
        };
        
        // Check if sport is Badminton or Table Tennis (normalize name: remove spaces, lowercase)
        // Handles values like "Table Tennis", "TableTennis", "tabletennis", "TT", "tt"
        const sport = getSport();
        const normalizedSport = sport?.replace(/\s+/g, '').toLowerCase();
        const isBadmintonOrTT = normalizedSport === 'badminton' ||
                                normalizedSport === 'tabletennis' ||
                                normalizedSport === 'tt';
        
        if (isBadmintonOrTT) {
          // Navigate to knockouts page for Badminton/TT
          navigate(`/manage-tournaments-fixtures/standings/knockouts/${tournamentId}/${id}?sport=${encodeURIComponent(sport)}&seasonId=${encodeURIComponent(tournamentSeasonId || '')}`, {
            state: { 
              ageCategoryInfo, 
              viewMode: true,
              tournamentId,
              tournamentSeasonId, // Pass tournamentSeasonId for back navigation
              fromTournamentDetailPage: !tournamentSeasonId || window.location.pathname.includes(`/manage-tournaments-fixtures/${tournamentId}`) && !window.location.pathname.includes('/seasons/'), // Check if we're on tournament detail page (not season detail page)
              selectedTournament: { sports: sport }
            }
          });
        } else if (ageCategory.standingsType === 'single') {
          navigate(`/manage-tournaments-fixtures/standings/single/${tournamentId}/${id}?sport=${encodeURIComponent(sport)}&seasonId=${encodeURIComponent(tournamentSeasonId || '')}`, {
            state: { 
              ageCategoryInfo, 
              viewMode: true,
              tournamentId,
              tournamentSeasonId, // Pass tournamentSeasonId for back navigation
              fromTournamentDetailPage: !tournamentSeasonId || window.location.pathname.includes(`/manage-tournaments-fixtures/${tournamentId}`) && !window.location.pathname.includes('/seasons/') // Check if we're on tournament detail page (not season detail page)
            }
          });
        } else if (ageCategory.standingsType === 'group') {
          navigate(`/manage-tournaments-fixtures/standings/group/${tournamentId}/${id}?sport=${encodeURIComponent(sport)}&seasonId=${encodeURIComponent(tournamentSeasonId || '')}`, {
            state: { 
              ageCategoryInfo, 
              viewMode: true,
              tournamentId,
              tournamentSeasonId, // Pass tournamentSeasonId for back navigation
              fromTournamentDetailPage: !tournamentSeasonId || window.location.pathname.includes(`/manage-tournaments-fixtures/${tournamentId}`) && !window.location.pathname.includes('/seasons/') // Check if we're on tournament detail page (not season detail page)
            }
          });
        } else {
          // Fallback to view form for other types
          setEditingId(id);
          setViewMode(true);
          setShowAddForm(true);
          setFormError(null);
          setFormSuccess(null);
          setForm({
            ageCategory: ageCategory.ageCategory || '',
            standingsType: ageCategory.standingsType || '',
            format: isPickleball() ? 'player' : ageCategory.format ? ageCategory.format.toLowerCase() : '',
            knockoutFormat: ageCategory.knockoutFormat || '',
            qualificationStatus: ageCategory.isQualificationApplied === true ? 'Qualified' : ageCategory.isQualificationApplied === false ? 'Not Qualified' : '',
            qualifiedTeams: ageCategory.qualifiedTeams?.toString() || ageCategory.qualificationTeamCount?.toString() || '',
            duration: ageCategory.duration || '',
            liveImage: ageCategory.liveImage || null,
            highlightsImage: ageCategory.highlightsImage || null,
            eventCategories: eventCategoriesForForm(ageCategory.eventCategories),
            isKnockoutEnabled: ageCategory.isKnockoutEnabled || false,
            awardCategory: typeof ageCategory.awardCategory === 'string' ? ageCategory.awardCategory : '',
            votingInfo: typeof ageCategory.votingInfo === 'string' ? ageCategory.votingInfo : '',
          });
          
          // Scroll to top for better user experience
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } else {
        // If in age categories context, open view form at the top (read-only mode)
        setEditingId(id);
        setViewMode(true);
        setShowAddForm(true);
        setFormError(null);
        setFormSuccess(null);
        setForm({
          ageCategory: ageCategory.ageCategory || '',
          standingsType: ageCategory.standingsType || '',
          format: isPickleball() ? 'player' : ageCategory.format ? ageCategory.format.toLowerCase() : '',
          knockoutFormat: ageCategory.knockoutFormat || '',
          qualificationStatus: ageCategory.isQualificationApplied === true ? 'Qualified' : ageCategory.isQualificationApplied === false ? 'Not Qualified' : '',
          qualifiedTeams: ageCategory.qualifiedTeams?.toString() || ageCategory.qualificationTeamCount?.toString() || '',
          duration: ageCategory.duration || '',
          liveImage: ageCategory.liveImage || null,
          highlightsImage: ageCategory.highlightsImage || null,
          eventCategories: eventCategoriesForForm(ageCategory.eventCategories),
          isKnockoutEnabled: ageCategory.isKnockoutEnabled || false,
          awardCategory: typeof ageCategory.awardCategory === 'string' ? ageCategory.awardCategory : '',
          votingInfo: typeof ageCategory.votingInfo === 'string' ? ageCategory.votingInfo : '',
        });
        
        // Scroll to top for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const handleSwitchToEdit = () => {
    setViewMode(false);
    setFormError(null);
    setFormSuccess(null);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      setFormError(null); // Clear any previous errors
      try {
        // Get sport from tournamentSports prop or default to 'Football'
        const sport = getSport();
        // FIX: Pass sport parameter to deleteAgeCategory
        await deleteAgeCategory(deleteId, sport);
        setShowDeleteModal(false);
        setDeleteId(null);
        setFormError(null); // Clear error on success
        onRefresh();
      } catch (err: any) {
        console.error('Delete age category error:', err);
        
        // Extract error message from API response
        let errorMessage = 'Failed to delete age category. Please try again.';
        
        if (err.response && err.response.data) {
          const errorData = err.response.data;
          // Priority: error > message > details > string
          if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          } else if (typeof errorData === 'string') {
            errorMessage = errorData;
          }
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        setFormError(errorMessage);
        // Keep modal open to show the error message
      }
    }
  };

  const resetForm = () => {
    setForm({
      ageCategory: '',
      standingsType: '',
      format: isPickleball() ? 'player' : '',
      knockoutFormat: '',
      qualificationStatus: '',
      qualifiedTeams: '',
      duration: '',
      liveImage: null,
      highlightsImage: null,
      eventCategories: [],
      isKnockoutEnabled: false,
      awardCategory: '',
      votingInfo: '',
    });
    setFormTouched({
      ageCategory: false,
      standingsType: false,
      format: false,
      duration: false,
      qualificationStatus: false,
      eventCategories: false,
    });
    setFormError(null);
    setFieldErrors({});
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setEditingId(null);
    resetForm();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setFormTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleSelectChange = (name: string, selectedOption: any) => {
    setForm(prev => ({ ...prev, [name]: selectedOption ? selectedOption.value : '' }));
    setFormTouched(prev => ({ ...prev, [name]: true }));
  };

  const handleImageUpload = async (file: File, type: 'liveImage' | 'highlightsImage') => {
    try {
      const response = await uploadAgeCategoryImage(file, type);
      setForm(prev => ({ ...prev, [type]: response }));
    } catch (err: any) {
      console.error('Image upload error:', err);
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.message) {
          setFormError(errorData.message);
        } else if (errorData.error) {
          setFormError(errorData.error);
        } else if (errorData.details) {
          setFormError(errorData.details);
        } else if (typeof errorData === 'string') {
          setFormError(errorData);
        } else {
          setFormError('Failed to upload image. Please try again.');
        }
      } else if (err.message) {
        setFormError(err.message);
      } else {
        setFormError('Failed to upload image. Please try again.');
      }
    }
  };

  const validateForm = () => {
    return {
      ageCategory: !form.ageCategory.trim() ? 'Age Category is required.' : '',
      standingsType: !form.standingsType ? 'Standing Type is required.' : '',
      format: !form.format ? 'Format is required.' : '',
      eventCategories: isPickleball() && (!Array.isArray(form.eventCategories) || form.eventCategories.length === 0)
        ? 'Select at least one event category.'
        : '',
      duration: '', // Duration is optional, no validation needed
      qualificationStatus: '' // Qualification Status is optional, no validation needed
    };
  };

  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  const handleSave = async () => {
    setFormTouched({
      ageCategory: true,
      standingsType: true,
      format: true,
      duration: true,
      qualificationStatus: true,
      eventCategories: true,
    });
    setFieldErrors({});
    
    if (!isFormValid) {
      return;
    }

    setFormError(null);
    setFormSuccess(null);
    setSaving(true);

    try {
      // Extract numeric value from duration (e.g., "30 min" -> "30", "1.5 hr" -> "1.5")
      const durationValue = getDurationPayloadValue(form.duration);
      
      const payload: any = {
        ageCategory: form.ageCategory,
        standingsType: form.standingsType,
        format: isPickleball() ? 'player' : form.format,
        knockoutFormat: form.knockoutFormat || undefined,
        isQualificationApplied: form.qualificationStatus === 'Qualified' ? true : form.qualificationStatus === 'Not Qualified' ? false : undefined,
        qualifiedTeams: form.qualifiedTeams ? parseInt(form.qualifiedTeams) : undefined,
        duration: durationValue,
        liveImage: form.liveImage,
        highlightsImage: form.highlightsImage,
        eventCategories: isPickleball()
          ? normalizePickleballEventCategories(form.eventCategories)
          : normalizeEventCategories(typeof form.eventCategories === 'string' ? form.eventCategories : form.eventCategories.join(', ')),
        isKnockoutEnabled: form.isKnockoutEnabled,
        awardCategory: isPickleball() ? [] : form.awardCategory.trim() || undefined,
        votingInfo: isPickleball()
          ? { image: null, startDate: null, endDate: null }
          : form.votingInfo.trim() || undefined,
      };
      
      // Always include tournamentId
      payload.tournament = tournamentId;
      
      // If tournamentSeasonId is provided (season context), also include it
      // This is REQUIRED for both add and edit operations in season context
      if (tournamentSeasonId) {
        payload.tournamentSeason = tournamentSeasonId;
        console.log('Including tournamentSeason in age category payload:', tournamentSeasonId, 'for', editingId ? 'edit' : 'add');
      } else {
        console.warn('tournamentSeasonId is not provided. This may cause errors in season context.');
        console.warn('Current props:', { tournamentId, tournamentSeasonId, editingId });
      }

      console.log('Age category payload:', JSON.stringify(payload, null, 2));
      console.log('Editing ID:', editingId);
      console.log('Is edit operation:', !!editingId);
      console.log('tournamentSeasonId prop value:', tournamentSeasonId);
      console.log('Payload includes tournamentSeason:', !!payload.tournamentSeason);

      let response;
      // Get sport from tournamentSports prop or default to 'Football'
      const sport = tournamentSports || 'Football';
      
      if (editingId) {
        console.log('Editing age category with ID:', editingId);
        // Double-check that tournamentSeason is included for edit operations
        if (!payload.tournamentSeason && tournamentSeasonId) {
          console.warn('WARNING: tournamentSeason missing from payload, adding it now');
          payload.tournamentSeason = tournamentSeasonId;
        }
        console.log('Final payload for edit:', JSON.stringify(payload, null, 2));
        // FIX: Pass sport parameter to editAgeCategory
        response = await editAgeCategory(editingId, payload, sport);
        setFormSuccess(response?.data?.message || 'Age category updated successfully!');
      } else {
        console.log('Adding new age category');
        // FIX: Pass sport parameter to addAgeCategory
        response = await addAgeCategory(payload, sport);
        setFormSuccess(response?.data?.message || 'Age category added successfully!');
      }

      // Clear form and close after a short delay to show success message
      setTimeout(() => {
        setShowAddForm(false);
        setEditingId(null);
        setViewMode(false);
        resetForm();
        setFormSuccess(null);
        onRefresh();
      }, 1500);
    } catch (err: any) {
      console.error('Age category save error:', err);
      
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        
        // Handle field-specific validation errors
        if (errorData.errors) {
          setFieldErrors(errorData.errors);
        }
        
        // Handle general API error messages
        if (errorData.message) {
          setFormError(errorData.message);
        } else if (errorData.error) {
          setFormError(errorData.error);
        } else if (errorData.details) {
          setFormError(errorData.details);
        } else if (typeof errorData === 'string') {
          setFormError(errorData);
        } else {
          // Handle specific error types
          if (errorData.validation) {
            setFormError(errorData.validation);
          } else if (errorData.detail) {
            setFormError(errorData.detail);
          } else {
            setFormError('An error occurred while saving the age category.');
          }
        }
      } else if (err.message) {
        setFormError(err.message);
      } else {
        setFormError('Failed to save age category. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const getQualificationStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'qualified':
        return <Badge bg="success">Qualified</Badge>;
      case 'not qualified':
        return <Badge bg="danger">Not Qualified</Badge>;
      default:
        return <Badge bg="secondary">-</Badge>;
    }
  };

  return (
    <div>
      {/* Header with Filters */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Age Category</h4>
        <div className="d-flex align-items-center gap-3">
          <div style={{ minWidth: 200, position: 'relative' }}>
            <Form.Control
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              style={{ 
                minHeight: '38px',
                borderRadius: '6px',
                fontSize: '14px',
                paddingRight: search ? '35px' : '12px'
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => onSearch('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          <div style={{ minWidth: 150 }}>
            <Select
              classNamePrefix="react-select"
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ value: '', label: 'All' }, ...typeOptions]}
              placeholder="Standing Type"
              isClearable
              styles={{ 
                control: base => ({ 
                  ...base, 
                  minHeight: '38px', 
                  borderRadius: '6px',
                  fontSize: '14px'
                })
              }}
            />
          </div>
          {!isStandingsContext && (
            <div style={{ minWidth: 150 }}>
              <Select
                classNamePrefix="react-select"
                value={formatFilter}
                onChange={setFormatFilter}
                options={[{ value: '', label: 'All' }, ...formatOptions]}
                placeholder="Format"
                isClearable
                styles={{ 
                  control: base => ({ 
                    ...base, 
                    minHeight: '38px', 
                    borderRadius: '6px',
                    fontSize: '14px'
                  })
                }}
              />
            </div>
          )}
          <Button 
            variant="primary" 
            onClick={handleAdd}
            style={{ borderRadius: '6px' }}
          >
            Add New +
          </Button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              {viewMode ? 'View Age Category' : editingId ? 'Edit Age Category' : 'Add Age Category'}
            </h5>
          </Card.Header>
          <Card.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            {formSuccess && <div className="alert alert-success">{formSuccess}</div>}
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age Category<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Form.Control
                      name="ageCategory"
                      value={form.ageCategory}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter Age Category"
                      isInvalid={!!errors.ageCategory && formTouched.ageCategory}
                      onBlur={() => setFormTouched(prev => ({ ...prev, ageCategory: true }))}
                    />
                    {errors.ageCategory && formTouched.ageCategory && (
                      <div className="text-danger">{errors.ageCategory}</div>
                    )}
                    {fieldErrors.ageCategory && (
                      <div className="text-danger">{fieldErrors.ageCategory}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Format<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={formatOptions.find(opt => opt.value === form.format) || null}
                      onChange={(selectedOption) => handleSelectChange('format', selectedOption)}
                      options={formatOptions}
                      placeholder="Select Format"
                      isClearable={false}
                      isDisabled={viewMode || isPickleball()}
                      styles={{ 
                        control: base => ({ 
                          ...base, 
                          minHeight: '38px',
                          borderColor: errors.format && formTouched.format ? '#dc3545' : base.borderColor
                        })
                      }}
                    />
                    {errors.format && formTouched.format && (
                      <div className="text-danger">{errors.format}</div>
                    )}
                    {fieldErrors.format && (
                      <div className="text-danger">{fieldErrors.format}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              {(isPickleball() || canConfigureTies || canConfigureAwards || canConfigureVoting) && <Row>
                {isPickleball() ? <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Categories<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      isMulti
                      value={PICKLEBALL_EVENT_CATEGORIES.filter((option) =>
                        Array.isArray(form.eventCategories) && form.eventCategories.includes(option.value)
                      )}
                      onChange={(selectedOptions) => {
                        setForm((current) => ({
                          ...current,
                          eventCategories: selectedOptions.map((option) => option.value),
                        }));
                        setFormTouched((current) => ({ ...current, eventCategories: true }));
                      }}
                      options={PICKLEBALL_EVENT_CATEGORIES}
                      placeholder="Select event categories"
                      isDisabled={viewMode}
                      styles={{
                        control: base => ({
                          ...base,
                          minHeight: '38px',
                          borderColor: errors.eventCategories && (formTouched as any).eventCategories ? '#dc3545' : base.borderColor
                        })
                      }}
                    />
                    {errors.eventCategories && (formTouched as any).eventCategories && (
                      <div className="text-danger">{errors.eventCategories}</div>
                    )}
                  </Form.Group>
                </Col> : canConfigureTies && <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Categories</Form.Label>
                    <Form.Control
                      name="eventCategories"
                      value={typeof form.eventCategories === 'string' ? form.eventCategories : form.eventCategories.join(', ')}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Singles, Doubles, Mixed Doubles"
                    />
                    <Form.Text className="text-muted">Separate multiple categories with commas.</Form.Text>
                  </Form.Group>
                </Col>}
                {canConfigureAwards && <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Award Category</Form.Label>
                    <Form.Control
                      name="awardCategory"
                      value={form.awardCategory}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="e.g. Player of the Tournament"
                    />
                  </Form.Group>
                </Col>}
                {canConfigureVoting && <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Voting Information</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="votingInfo"
                      value={form.votingInfo}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter voting details"
                    />
                  </Form.Group>
                </Col>}
                {canConfigureTies && <Col md={12}>
                  <Form.Check
                    type="switch"
                    id="is-knockout-enabled"
                    label="Enable knockout stage"
                    checked={form.isKnockoutEnabled}
                    onChange={(event) => setForm((current) => ({ ...current, isKnockoutEnabled: event.target.checked }))}
                    disabled={viewMode}
                  />
                </Col>}
              </Row>}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Knockout Format</Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={knockoutFormatOptions.find(opt => opt.value === form.knockoutFormat) || null}
                      onChange={(selectedOption) => handleSelectChange('knockoutFormat', selectedOption)}
                      options={knockoutFormatOptions}
                      placeholder="Select Knockout Format"
                      isClearable={true}
                      isDisabled={viewMode}
                      styles={{ 
                        control: base => ({ 
                          ...base, 
                          minHeight: '38px'
                        })
                      }}
                    />
                    {fieldErrors.knockoutFormat && (
                      <div className="text-danger">{fieldErrors.knockoutFormat}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Sports</Form.Label>
                    <Form.Control
                      value={tournamentSports}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tournament</Form.Label>
                    <Form.Control
                      value={tournamentName}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration of Each Match (Optional)</Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={durationOptions.find(opt => opt.value === form.duration) || null}
                      onChange={(selectedOption) => handleSelectChange('duration', selectedOption)}
                      options={durationOptions}
                      placeholder="Select Duration"
                      isClearable={true}
                      isDisabled={viewMode}
                      styles={{ 
                        control: base => ({ 
                          ...base, 
                          minHeight: '38px',
                          borderColor: errors.duration && formTouched.duration ? '#dc3545' : base.borderColor
                        })
                      }}
                    />
                    {errors.duration && formTouched.duration && (
                      <div className="text-danger">{errors.duration}</div>
                    )}
                    {fieldErrors.duration && (
                      <div className="text-danger">{fieldErrors.duration}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Standing Type<span style={{ color: 'red' }}>*</span></Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={typeOptions.find(opt => opt.value === form.standingsType) || null}
                      onChange={(selectedOption) => handleSelectChange('standingsType', selectedOption)}
                      options={typeOptions}
                      placeholder="Select Standing Type"
                      isClearable={false}
                      isDisabled={viewMode}
                      styles={{ 
                        control: base => ({ 
                          ...base, 
                          minHeight: '38px',
                          borderColor: errors.standingsType && formTouched.standingsType ? '#dc3545' : base.borderColor
                        })
                      }}
                    />
                    {errors.standingsType && formTouched.standingsType && (
                      <div className="text-danger">{errors.standingsType}</div>
                    )}
                    {fieldErrors.standingsType && (
                      <div className="text-danger">{fieldErrors.standingsType}</div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Qualification Status (Optional)</Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      value={qualificationStatusOptions.find(opt => opt.value === form.qualificationStatus) || null}
                      onChange={(selectedOption) => handleSelectChange('qualificationStatus', selectedOption)}
                      options={qualificationStatusOptions}
                      placeholder="Select Status"
                      isClearable={true}
                      isDisabled={viewMode}
                      styles={{ 
                        control: base => ({ 
                          ...base, 
                          minHeight: '38px',
                          borderColor: errors.qualificationStatus && formTouched.qualificationStatus ? '#dc3545' : base.borderColor
                        })
                      }}
                    />
                    {errors.qualificationStatus && formTouched.qualificationStatus && (
                      <div className="text-danger">{errors.qualificationStatus}</div>
                    )}
                    {fieldErrors.qualificationStatus && (
                      <div className="text-danger">{fieldErrors.qualificationStatus}</div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Qualified Teams (Optional)</Form.Label>
                    <Form.Control
                      name="qualifiedTeams"
                      type="number"
                      value={form.qualifiedTeams}
                      onChange={handleInputChange}
                      readOnly={viewMode}
                      placeholder="Enter number of teams"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Highlight Images (Optional)</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="border rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '60px', height: '60px', backgroundColor: '#f8f9fa' }}
                      >
                        {form.highlightsImage ? (
                          <img 
                            src={getImageUrl(form.highlightsImage)} 
                            alt="Highlight" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                            onError={(e) => {
                              console.error('Highlights image failed to load:', form.highlightsImage);
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                            onLoad={() => {
                              console.log('Highlights image loaded successfully:', form.highlightsImage);
                            }}
                          />
                        ) : (
                          <i className="ri-image-line" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={viewMode}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, 'highlightsImage');
                        }}
                        className="form-control"
                        style={{ width: 'auto' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Normal Match Banner (Optional)</Form.Label>
                    <div className="d-flex align-items-center gap-3">
                      <div 
                        className="border rounded-circle d-flex align-items-center justify-content-center"
                        style={{ width: '60px', height: '60px', backgroundColor: '#f8f9fa' }}
                      >
                        {form.liveImage ? (
                          <img 
                            src={getImageUrl(form.liveImage)} 
                            alt="Live" 
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                            onError={(e) => {
                              console.error('Live image failed to load:', form.liveImage);
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                            onLoad={() => {
                              console.log('Live image loaded successfully:', form.liveImage);
                            }}
                          />
                        ) : (
                          <i className="ri-image-line" style={{ fontSize: '1.5rem', color: '#6c757d' }}></i>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={viewMode}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file, 'liveImage');
                        }}
                        className="form-control"
                        style={{ width: 'auto' }}
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>
              <div className="d-flex justify-content-end gap-2">
                {viewMode ? (
                  <>
                    <Button variant="outline-secondary" onClick={handleCancel}>
                      Close
                    </Button>
                    <Button variant="primary" onClick={handleSwitchToEdit}>
                      Edit
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline-secondary" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button 
                      variant="primary" 
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : editingId ? 'Update Age Category' : 'Add Age Category'}
                    </Button>
                  </>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>
      )}

      {/* Table */}
      <div className="manage-tournaments-table-container">
        <Table className={`mb-0 manage-tournaments-table age-category-table ${isStandingsContext ? 'standings-context' : ''}`}>
            <thead>
              <tr>
                <th>Age Category</th>
                {!isStandingsContext && <th>Format</th>}
                <th>Standing Type</th>
                {!isStandingsContext && <th>Qualification Status</th>}
                {!isStandingsContext && <th>Qualification Teams</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ageCategoriesLoading ? (
                <tr>
                  <td colSpan={isStandingsContext ? 3 : 6}><ShimmerLoader type="table" rows={5} /></td>
                </tr>
              ) : filteredAgeCategories.length === 0 ? (
                <tr>
                  <td colSpan={isStandingsContext ? 3 : 6}>
                    <div className="text-center py-5">
                      <div className="empty-state">
                        <div className="empty-state-icon">
                          <i className="ph-users" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                        </div>
                        <div className="empty-state-title">No Age Categories Found</div>
                        <div className="empty-state-description">
                          There are no age categories available for this tournament.
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAgeCategories.map((ac) => (
                  <tr key={ac._id}>
                    <td>{ac.ageCategory}</td>
                    {!isStandingsContext && <td>{formatOptions.find(opt => opt.value === ac.format)?.label || ac.format || '-'}</td>}
                    <td>{typeOptions.find(opt => opt.value === ac.standingsType)?.label || ac.standingsType}</td>
                    {!isStandingsContext && <td>{ac.isQualificationApplied === true ? 'Qualified' : ac.isQualificationApplied === false ? 'Not Qualified' : '-'}</td>}
                    {!isStandingsContext && <td>{ac.qualificationTeamCount || '-'}</td>}
                    <td>
                      <div className="tournament-actions">
                        <button
                          className="action-btn btn-view"
                          onClick={() => handleView(ac._id)}
                          title="View Age Category"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                    {isStandingsContext && isBadminton() && tournamentSeasonId && (
                      <button
                        className="action-btn btn-edit"
                        onClick={() => navigate(
                          `/manage-tournaments-fixtures/${tournamentId}/seasons/${tournamentSeasonId}/ties/${ac._id}?sport=Badminton`,
                          { state: { selectedTournament: { sports: 'Badminton' }, tournamentSeasonId } }
                        )}
                        title="Manage Badminton Ties"
                      >
                        <i className="ri-links-line"></i>
                      </button>
                    )}
                    {/* In Standings tab for Badminton/Table Tennis, hide Edit/Delete to prevent navigation */}
                    {!(isStandingsContext && isBadmintonOrTableTennis()) && (
                      <>
                        <button
                          className="action-btn btn-edit"
                          onClick={() => handleEdit(ac._id)}
                          title="Edit Age Category"
                          disabled={loadingAgeCategory}
                        >
                          {loadingAgeCategory && editingId === ac._id ? (
                            <i className="ri-loader-4-line ri-spin"></i>
                          ) : (
                            <i className="ri-pencil-line"></i>
                          )}
                        </button>
                        <button
                          className="action-btn btn-delete"
                          onClick={() => handleDelete(ac._id)}
                          title="Delete Age Category"
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      </>
                    )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

      {/* Pagination Controls - Match Manage Players */}
      {shouldShowPagination && (
        <div className="pagination-section mt-4" style={{ 
          padding: '16px 0',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {/* Page Info */}
          <div className="page-info mb-3">
            <span className="text-muted" style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: '#6c757d'
            }}>
              Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} age categories
            </span>
          </div>

          {/* Pagination Controls */}
          <div className="pagination-controls d-flex align-items-center">
            <div className="page-numbers d-flex align-items-center">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                style={{ marginRight: '8px' }}
              >
                Previous
              </Button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else {
                  const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                  pageNum = startPage + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={pageNum === page ? "primary" : "outline-secondary"}
                    size="sm"
                    onClick={() => onPageChange(pageNum)}
                    style={{ marginRight: '4px', minWidth: '32px' }}
                  >
                    {pageNum}
                  </Button>
                );
              })}
              
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
                style={{ marginLeft: '8px' }}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        handleClose={() => {
          setShowDeleteModal(false);
          setDeleteId(null);
          setFormError(null); // Clear error when closing modal
        }}
        deleteModalFunction={confirmDelete}
        title="Delete Age Category"
        message="Are you sure you want to delete this age category? This action cannot be undone."
        error={formError}
      />
    </div>
  );
};

export default AgeCategoryManagement;
